import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAdminAuth } from '@/store/admin/admin-auth-slice';
import { readReturnTo } from '@/auth/return-to';

/**
 * Admin login page.
 *
 * Posts to `/connect/token` (IdentityServer OAuth2 password grant).
 * client_id / scope / grant_type match the scheduling app's existing
 * `src/services/dev-config.ts`. The specific `Scrips.Admin` client may differ
 * once Identity is updated — verified on first real call.
 *
 * On success: stores tokens via the Zustand slice, which decodes the JWT and
 * computes role. The effect pushes the user to either /admin/super,
 * /admin/org, or /admin/403 based on role.
 */
export function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken, role, setTokens } = useAdminAuth();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setSubmitting] = useState(false);

  // Already signed in — bounce to the role home.
  if (accessToken && role !== 'none') {
    const from = readReturnTo(
      location.state,
      role === 'superAdmin' ? '/admin/super' : '/admin/org',
    );
    return <Navigate to={from} replace />;
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch('/connect/token', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          grant_type: 'password',
          client_id: 'Scrips.Admin',
          scope: 'openid offline_access',
          username,
          password,
        }),
      });
      if (!res.ok) {
        setError('Invalid credentials. Please try again.');
        return;
      }
      const body = (await res.json()) as {
        access_token: string;
        refresh_token?: string;
      };
      setTokens(body.access_token, body.refresh_token ?? null);
      // After setTokens the slice computes role; useEffect not needed, just navigate.
      const next = useAdminAuth.getState().role;
      navigate(next === 'superAdmin' ? '/admin/super' : '/admin/org', { replace: true });
    } catch {
      setError('Sign-in failed — please check your connection and retry.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-[var(--surface-app)] px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-card border border-[var(--surface-line)] bg-[var(--color-surface-card)] p-8 flex flex-col gap-4"
        aria-label="Admin sign-in"
      >
        <div className="flex items-center gap-3 mb-2">
          <div className="size-10 rounded-md bg-[var(--accent-primary)] flex items-center justify-center text-[var(--surface-read)] [font:var(--type-card-title)]">
            S
          </div>
          <div>
            <div className="[font:var(--type-card-title)] text-[var(--ink-body)]">Scrips Admin</div>
            <div className="[font:var(--type-body)] text-[var(--ink-secondary)]">Sign in to continue</div>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="login-username">Email</Label>
          <Input
            id="login-username"
            type="email"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            required
            disabled={isSubmitting}
          />
        </div>

        <div className="flex flex-col gap-2">
          <Label htmlFor="login-password">Password</Label>
          <Input
            id="login-password"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={isSubmitting}
          />
        </div>

        {error && (
          <div
            role="alert"
            className="[font:var(--type-body)] text-[var(--status-bad)] bg-[var(--status-overdue-bg)] rounded-md px-3 py-2"
          >
            {error}
          </div>
        )}

        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Signing in…' : 'Sign in'}
        </Button>
      </form>
    </div>
  );
}
